/*
 * © 2026 Renishaw plc. All rights reserved.
 * This source file is the confidential property and copyright of Renishaw plc
 * Reproduction or transmission in whole or in part, in any form or
 * by any means, electronic, mechanical or otherwise, is prohibited
 * without the prior written consent of the copyright owner.
 */
import { sortBy } from "lodash/fp";
import { MeasurementCharacteristicPresentation } from "@/presentation/MeasurementCharacteristic";
import { MeasurementSortDirection } from "./rowOrder";

/**
 * Sorting for the measurement *detail* table only. The summary
 * table has its own columns and its own sorter in ./statistics - deliberately
 * kept apart so neither table's cases leak into the other's.
 *
 * The two tables stay in step through the shared row order (see ./rowOrder),
 * not through a shared sort function.
 */
export const measurementSortColumns = [
  "Feature",
  "Name",
  "Actual",
  "Nominal",
  "Lower Tol.",
  "Upper Tol.",
  "Deviation",
  "Error",
  "Graphic",
] as const;

export type MeasurementSortColumn = (typeof measurementSortColumns)[number];

type SortValue = string | number | null | undefined;

/**
 * The graphic column plots deviation against the tolerance band, so it orders
 * by distance from nominal regardless of direction.
 */
const absoluteNormalisedDeviation = (
  row: MeasurementCharacteristicPresentation
): SortValue =>
  row.normalisedDeviation !== undefined
    ? Math.abs(row.normalisedDeviation)
    : null;

const columnValues: Record<
  MeasurementSortColumn,
  (row: MeasurementCharacteristicPresentation) => SortValue
> = {
  Feature: (row) => row.featureName,
  Name: (row) => row.name,
  Actual: (row) => row.actual,
  Nominal: (row) => row.nominal,
  "Lower Tol.": (row) => row.lowerLimit,
  "Upper Tol.": (row) => row.upperLimit,
  Deviation: (row) => row.deviation,
  Error: (row) => row.error,
  Graphic: absoluteNormalisedDeviation,
};

export function isMeasurementSortColumn(
  column: string
): column is MeasurementSortColumn {
  return (measurementSortColumns as readonly string[]).includes(column);
}

/** Sorts the measurement detail table's rows by one of its own columns. */
export function sortMeasurements(
  rows: readonly MeasurementCharacteristicPresentation[],
  sortColumn: string,
  sortDirection: MeasurementSortDirection
): MeasurementCharacteristicPresentation[] {
  const getValue = isMeasurementSortColumn(sortColumn)
    ? columnValues[sortColumn]
    : (row: MeasurementCharacteristicPresentation) => row.normalisedDeviation;

  const sorted = sortBy(getValue)([...rows]);

  return sortDirection === "desc" ? sorted.reverse() : sorted;
}
